import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Search } from "lucide-react";
import { ARTICLES, CATEGORIES, formatDate } from "@/data/articles";
import { Seo, breadcrumbSchema } from "@/components/layout/Seo";
import { PageHero, Reveal } from "@/components/ui/motion";

const Card = ({ a, i, big }) => (
  <Link to={`/artikel/${a.slug}`} className={`card group flex h-full flex-col p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-sky-brand sm:p-8 ${big ? "bg-slateb" : ""}`} data-testid={`article-card-${a.slug}`}>
    <div className="flex items-center justify-between text-[12px] font-bold uppercase tracking-[0.16em]">
      <span className={big ? "text-sky-brand" : "text-accentb"}>{a.category}</span>
      <span className={big ? "text-ice" : "text-ink-soft"}>{String(i + 1).padStart(2, "0")}</span>
    </div>
    <h2 className={`mt-8 font-display font-bold leading-tight ${big ? "text-3xl !text-white sm:text-4xl" : "text-xl sm:text-2xl"}`}>{a.title}</h2>
    <p className={`mt-4 text-[15px] leading-relaxed ${big ? "text-ice" : "text-ink"}`}>{a.excerpt}</p>
    <div className={`mt-auto flex items-center justify-between pt-8 text-[13px] ${big ? "text-ice" : "text-ink-soft"}`}>
      <span>{formatDate(a.date)} · {a.readTime} menit baca</span>
      <ArrowUpRight size={18} className={`transition-transform duration-300 group-hover:rotate-45 ${big ? "text-sky-brand" : "text-accentb"}`} />
    </div>
  </Link>
);

export default function Articles() {
  const [cat, setCat] = useState("Semua");
  const [q, setQ] = useState("");
  const list = useMemo(
    () => ARTICLES.filter((a) => (cat === "Semua" || a.category === cat) && (a.title + a.excerpt).toLowerCase().includes(q.toLowerCase())),
    [cat, q]
  );
  const crumbs = [{ to: "/", label: "Beranda" }, { to: "/artikel", label: "Artikel" }];
  return (
    <>
      <Seo title="Artikel Edukasi Penyelesaian Utang Pinjol, Kartu Kredit & KTA | Mika Solution" description="Panduan edukatif seputar pinjaman online, kartu kredit, KTA, dan cara memahami pilihan penyelesaian kewajiban finansial secara bijak." path="/artikel" schema={[breadcrumbSchema(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Pusat Edukasi" lines={["Pahami Dulu,", <span className="serif-it font-normal text-accentb">Putuskan Kemudian.</span>]} desc="Artikel edukasi untuk membantu Anda memahami kewajiban finansial dan pilihan penyelesaiannya." />
      <section className="py-16 sm:py-24" data-testid="articles-hub">
        <div className="wrap">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Kategori artikel">
              {CATEGORIES.map((c) => (
                <button key={c} role="tab" aria-selected={cat === c} onClick={() => setCat(c)} data-testid={`category-${c.toLowerCase().replace(/\s+/g, "-")}`} className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-colors duration-300 ${cat === c ? "bg-slateb text-white" : "border border-line text-slateb hover:border-sky-brand"}`}>
                  {c}
                </button>
              ))}
            </div>
            <label className="relative block w-full lg:w-80">
              <span className="sr-only">Cari artikel</span>
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari artikel…" className="field !rounded-full !py-3 !pl-11" data-testid="articles-search" />
            </label>
          </div>
          <motion.div layout className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {list.map((a, i) => (
              <Reveal key={a.slug} delay={(i % 3) * 0.06} className={i === 0 && cat === "Semua" && !q ? "md:col-span-2" : ""}>
                <Card a={a} i={i} big={i === 0 && cat === "Semua" && !q} />
              </Reveal>
            ))}
          </motion.div>
          {!list.length && <p className="mt-12 text-ink" data-testid="articles-empty">Belum ada artikel yang cocok dengan pencarian Anda.</p>}
        </div>
      </section>
    </>
  );
}
